
import { supabase } from "@/integrations/supabase/client";
import { AIStaffMember } from "@/lib/types";
import { logger } from "@/lib/logger";
import { BaseApiClient } from "../../baseApiClient";
import { staffMapper } from "../mappers";

/**
 * Fetch all active staff for a business
 */
export const fetchAllStaff = async (businessId: string): Promise<AIStaffMember[]> => {
  return BaseApiClient.request<AIStaffMember[]>(
    `staff:all:${businessId}`,
    async () => {
      try {
        logger.info({
          message: "Fetching all active staff",
          context: { businessId }
        });
        
        const { data, error } = await supabase
          .from('ai_staff')
          .select('*')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .order('hired_at', { ascending: false });
        
        if (error) {
          throw new Error(`Failed to fetch staff: ${error.message}`);
        }
        
        // Map each database record to the AIStaffMember type
        return (data || []).map(record => staffMapper.toFrontend(record));
      } catch (error) {
        logger.error({
          message: "Error fetching all staff",
          error: error instanceof Error ? error : new Error(String(error)),
          context: { businessId }
        });
        throw error;
      }
    },
    { cache: { enabled: true, duration: 60000 } } // 1 minute cache
  );
};

/**
 * Fetch a specific staff member by ID
 */
export const fetchStaffById = async (businessId: string, staffId: string): Promise<AIStaffMember | null> => {
  return BaseApiClient.request<AIStaffMember | null>(
    `staff:${businessId}:${staffId}`,
    async () => {
      try {
        logger.info({
          message: "Fetching specific staff",
          context: { businessId, staffId }
        });
        
        const { data, error } = await supabase
          .from('ai_staff')
          .select('*')
          .eq('business_id', businessId)
          .eq('id', staffId)
          .maybeSingle();
        
        if (error) {
          throw new Error(`Failed to fetch staff: ${error.message}`);
        }
        
        // Map the database record to the AIStaffMember type
        return data ? staffMapper.toFrontend(data) : null;
      } catch (error) {
        logger.error({
          message: "Error fetching specific staff",
          error: error instanceof Error ? error : new Error(String(error)),
          context: { businessId, staffId }
        });
        throw error;
      }
    },
    { cache: { enabled: true, duration: 60000 } } // 1 minute cache
  );
};
